import { DateTime } from 'luxon'

const DISPLAY_TIMEZONE = 'America/Sao_Paulo'

export const parseBrDate = (maskedDate: string): DateTime | null => {
  const trimmed = maskedDate.trim()
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return null

  const parsed = DateTime.fromFormat(trimmed, 'dd/MM/yyyy')
  return parsed.isValid ? parsed.startOf('day') : null
}

export const parseFlexibleDate = (value: string): DateTime | null => {
  const trimmed = value.trim()
  if (!trimmed) return null

  const brDate = parseBrDate(trimmed)
  if (brDate) return brDate

  const isoDate = DateTime.fromISO(trimmed)
  if (isoDate.isValid) return isoDate.startOf('day')

  const jsDate = new Date(trimmed)
  if (!isNaN(jsDate.getTime())) {
    return DateTime.fromJSDate(jsDate).startOf('day')
  }

  return null
}

export const isDateBeforeToday = (value: string): boolean => {
  const parsed = parseFlexibleDate(value)
  if (!parsed) return false
  return parsed < DateTime.now().startOf('day')
}

export const formatDateForApi = (value: string): string => {
  const parsed = parseFlexibleDate(value)
  if (!parsed) return value.trim()

  const utcDate = DateTime.utc(parsed.year, parsed.month, parsed.day)
  return utcDate.toISO() ?? value.trim()
}

export const formatDateForDisplay = (value?: string): string => {
  if (!value?.trim()) return ''

  const parsed = parseFlexibleDate(value)
  if (parsed) return parsed.toFormat('dd/MM/yyyy')

  return value.trim()
}

export const formatTimeForDisplay = (value?: string): string => {
  if (!value?.trim()) return ''

  const trimmed = value.trim()
  const match = trimmed.match(/^(\d{1,2}):(\d{2})/)
  if (match) {
    return `${match[1].padStart(2, '0')}:${match[2]}`
  }

  return trimmed
}

export const formatDateTimeForDisplay = (value?: string): string => {
  if (!value?.trim()) return ''

  const parsed = DateTime.fromISO(value.trim(), { zone: 'utc' })
  if (parsed.isValid) {
    return parsed.setZone(DISPLAY_TIMEZONE).toFormat('dd/MM/yyyy, HH:mm')
  }

  const jsDate = new Date(value)
  if (!isNaN(jsDate.getTime())) {
    return DateTime.fromJSDate(jsDate, { zone: 'utc' })
      .setZone(DISPLAY_TIMEZONE)
      .toFormat('dd/MM/yyyy, HH:mm')
  }

  return value.trim()
}

export const toIsoDateOrNow = (value?: string): string => {
  const parsed = parseFlexibleDate(value ?? '')
  return parsed?.toISODate() ?? new Date().toISOString()
}
